import { Link } from "react-router-dom";
import {
  Camera, FileText, FileSpreadsheet, FileType2, MessageSquare, Cloud,
  ShieldCheck, Languages, Smartphone, Cpu, Check, ArrowRight, BookOpen
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, H2, Lead, FadeIn } from "../components/Primitives";
import { REGISTER_TYPES, EXPORT_OPTIONS } from "../data/content";

const exportIcons = {
  "PDF": FileText,
  "Excel": FileSpreadsheet,
  "Google Sheets": FileSpreadsheet,
  "Word": FileType2,
  "WhatsApp": MessageSquare,
};

const deepFeatures = [
  { icon: Cpu, title: "Indic OCR engine", body: "Trained on Devanagari and Latin scripts. Reads cursive Hindi handwriting and printed registers with 99% accuracy." },
  { icon: Cloud, title: "Encrypted cloud backup", body: "Every register is encrypted at rest and in transit. Backups run automatically — no IT team required." },
  { icon: Smartphone, title: "Mobile-first capture", body: "Built for Android and iOS first. Capture, crop and digitize without leaving your camera roll." },
  { icon: Languages, title: "Hindi + English", body: "Switch between हिंदी and English at the row level. Mixed-language registers are handled natively." },
  { icon: ShieldCheck, title: "Privacy by default", body: "Your data is yours. Export or delete anytime. We never train on customer data." },
  { icon: BookOpen, title: "Audit trail", body: "Every edit is timestamped. See who changed what and when, across every register." },
];

export default function FeaturesPage() {
  return (
    <div data-testid="page-features">
      <Section className="pt-24">
        <FadeIn><Eyebrow>Features</Eyebrow></FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">A full digital backbone for your paper registers.</H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6">
            One app to capture, structure, search and share every kind of register your business runs on.
          </Lead>
        </FadeIn>
      </Section>

      {/* Register types */}
      <Section testId="features-register-types" className="bg-zinc-50 border-y border-zinc-200">
        <div className="grid gap-6 md:grid-cols-2 md:items-end">
          <div>
            <Eyebrow>Register types</Eyebrow>
            <H2 className="mt-4">If it&apos;s a register, we support it.</H2>
          </div>
          <Lead>From attendance to OPD, our templates auto-detect the columns the moment you take a photo.</Lead>
        </div>
        <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {REGISTER_TYPES.map((r, i) => (
            <FadeIn key={r} delay={i * 0.03}>
              <div
                data-testid={`register-type-${r.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
                className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-zinc-950"
              >
                <Check className="h-4 w-4 text-zinc-950" />
                <span className="font-medium text-zinc-900">{r}</span>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Export options */}
      <Section testId="features-exports">
        <div className="grid gap-6 md:grid-cols-2 md:items-end">
          <div>
            <Eyebrow>Export anywhere</Eyebrow>
            <H2 className="mt-4">Your data, your formats.</H2>
          </div>
          <Lead>Export a register in any format your team already uses. Or share it directly to WhatsApp — no apps to install.</Lead>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {EXPORT_OPTIONS.map((opt) => {
            const Icon = exportIcons[opt] || FileText;
            return (
              <div key={opt} data-testid={`export-${opt.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-2xl border border-zinc-200 bg-white p-6 text-center">
                <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-950 text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-poppins text-base font-semibold text-zinc-950">{opt}</div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Deep features grid */}
      <Section testId="features-deep" className="bg-zinc-50 border-y border-zinc-200">
        <div className="grid gap-6 md:grid-cols-2 md:items-end">
          <div>
            <Eyebrow>Under the hood</Eyebrow>
            <H2 className="mt-4">Quietly powerful where it matters.</H2>
          </div>
          <Lead>The boring stuff (security, backups, audit logs) — done right so you can forget about it.</Lead>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {deepFeatures.map((f) => (
            <div key={f.title} className="rounded-2xl border border-zinc-200 bg-white p-7">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-950 text-white">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-poppins text-lg font-semibold text-zinc-950">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* OCR explainer */}
      <Section testId="features-ocr">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>OCR Technology</Eyebrow>
            <H2 className="mt-4">Built for Indian handwriting.</H2>
            <p className="mt-6 text-base leading-relaxed text-zinc-600">
              Most OCR engines were trained on English documents from US offices. Ours wasn&apos;t. We trained on tens of thousands
              of Indian registers — attendance ledgers from rural schools, OPD records from district hospitals, stock books from
              kirana stores. The result: an OCR engine that actually understands the way India writes.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Devanagari + Latin script support",
                "Cursive and printed text recognition",
                "Column auto-detection",
                "Signature & checkmark detection",
                "Mixed Hindi-English row support",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-zinc-700">
                  <Check className="mt-0.5 h-4 w-4 text-zinc-950" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/contact" data-testid="features-ocr-cta">
              <Button className="mt-7 rounded-full bg-zinc-950 text-white hover:bg-zinc-800">Try it on your register <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </Link>
          </div>
          <div className="relative rounded-3xl border border-zinc-200 bg-white p-6">
            <div className="mb-4 flex items-center gap-2 text-xs text-zinc-500">
              <Camera className="h-4 w-4" /> Photo captured · processing…
            </div>
            <div className="grid grid-cols-12 gap-2 text-xs text-zinc-400">
              <div className="col-span-2">Row</div>
              <div className="col-span-5">हिंदी</div>
              <div className="col-span-5">English</div>
            </div>
            <div className="mt-2 divide-y divide-zinc-100">
              {[
                ["01","रोहन वर्मा","Rohan Verma"],
                ["02","दीया पटेल","Diya Patel"],
                ["03","कबीर मेहता","Kabir Mehta"],
                ["04","अनन्या सिंह","Ananya Singh"],
              ].map((r) => (
                <div key={r[0]} className="grid grid-cols-12 gap-2 py-2.5 text-sm">
                  <div className="col-span-2 text-zinc-400">{r[0]}</div>
                  <div className="col-span-5 text-zinc-900">{r[1]}</div>
                  <div className="col-span-5 text-zinc-700">{r[2]}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1 text-[11px] text-zinc-600">
              <ShieldCheck className="h-3.5 w-3.5" /> 99% confidence
            </div>
          </div>
        </div>
      </Section>

      <Section className="pb-32">
        <div className="rounded-3xl bg-zinc-950 p-10 md:p-14 text-center">
          <h2 className="font-poppins text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Ready to retire that paper register?
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="features-final-cta">
              <Button size="lg" className="rounded-full bg-white px-6 text-zinc-950 hover:bg-zinc-100">Start free trial <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </Link>
            <Link to="/pricing" data-testid="features-pricing-cta">
              <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">See pricing</Button>
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
